/**
 * bridgeReducers — small Redux reducers that bridge the new Calendar Month
 * bootstrap/refs slices into the **legacy** top-level Redux keys that the
 * existing `CalendarNav` and `EventModals` modules consume via `connect()`.
 *
 * Why a bridge: per Phase 1 scope rules we cannot modify
 * `source/js/modules/calendar/calendar.nav.js` or anything in
 * `source/js/modules/calendar/modals/`. Both modules read from top-level
 * state slots (`state.organization`, `state.user`, `state.resources`,
 * `state.locations`, `state.customFields`, `state.timezone`, …) that the
 * legacy day-view entrypoint used to populate via SSR. Now that the v2
 * entrypoint fetches everything via Symfony, we need to feed those legacy
 * slots from the new slices without touching the consumers.
 *
 * Each bridge reducer listens to the corresponding `createAsyncThunk`
 * fulfilled action and returns the new value; otherwise it returns prior
 * state unchanged. The store wires these reducers AFTER the legacy
 * `calendar-day.reducers` spread so they take precedence (rightmost-wins
 * object spread).
 *
 * This file is intentionally tiny and side-effect-free so it is easy to
 * unit-test without `react-redux` or jsdom.
 *
 * @module bridgeReducers
 */

import { fetchBootstrap } from './bootstrapSlice.js';
import { fetchRefs, fetchCustomFields, fetchMechanics } from './refsSlice.js';
import { normalizeAccessGroups } from '../../utils/normalizeAccessGroups.js';

const BOOT_OK = fetchBootstrap.fulfilled.type;
const REFS_OK = fetchRefs.fulfilled.type;
const MECHANICS_OK = fetchMechanics.fulfilled.type;
const CF_OK = fetchCustomFields.fulfilled.type;

/**
 * Build a reducer that picks one field from a fulfilled action's payload.
 * Returns prior state for every other action so it composes cleanly with
 * `combineReducers`.
 */
function makeBridge(actionType, payloadKey, defaultValue = null) {
	return function bridge(state = defaultValue, action) {
		if (action && action.type === actionType) {
			const next = action.payload ? action.payload[payloadKey] : undefined;
			return next === undefined ? state : next;
		}
		return state;
	};
}

// ── Bootstrap-derived bridges (organization / user / timezone / orgs) ────────
export const organizationBridge = makeBridge(BOOT_OK, 'organization', {});
export const userBridge = makeBridge(BOOT_OK, 'user');
export const timezoneBridge = makeBridge(BOOT_OK, 'timezone');

/**
 * Normalize a Symfony array into the legacy `{ map, list }` pair that
 * `normalizeData()` from `api-generators.js:89-100` produces. The map is
 * keyed by `byField` (simple string) or a composite key when `byField` is
 * a two-element array and `compositeKey` is true.
 *
 * Optionally renames fields via `fieldMap` so Symfony DTO names (`id`,
 * `name`, `unique`) become the legacy names (`organizationID`,
 * `organizationName`, `organizationUnique`).
 */
export function normalizeToMapList(array, byField, compositeKey = false, fieldMap = null) {
	if (!Array.isArray(array) || array.length === 0) return { map: {}, list: [] };
	const map = {};
	const list = [];
	array.forEach(item => {
		if (!item) return;
		const mapped = fieldMap ? { ...item } : item;
		if (fieldMap) {
			Object.entries(fieldMap).forEach(([from, to]) => {
				if (from in mapped && !(to in mapped)) {
					mapped[to] = mapped[from];
				}
			});
		}
		const key = compositeKey
			? `${mapped[byField[0]]}-${mapped[byField[1]]}`
			: mapped[byField];
		if (key != null) {
			map[key] = mapped;
			list.push(key);
		}
	});
	return { map, list };
}

// Symfony → legacy field mapping for organizations
const ORG_FIELD_MAP = {
	id: 'organizationID',
	name: 'organizationName',
	unique: 'organizationUnique'
};

export function organizationsMapBridge(state = {}, action) {
	if (action && action.type === BOOT_OK) {
		const orgs = (action.payload && action.payload.userOrgs) || [];
		return normalizeToMapList(orgs, 'organizationID', false, ORG_FIELD_MAP).map;
	}
	return state;
}

export function organizationsListBridge(state = [], action) {
	if (action && action.type === BOOT_OK) {
		const orgs = (action.payload && action.payload.userOrgs) || [];
		return normalizeToMapList(orgs, 'organizationID', false, ORG_FIELD_MAP).list;
	}
	return state;
}

// `accessgroups` (lowercase, legacy spelling) is the per-user permission map
// the legacy modal code reads at `calendar.event-add.js:1068-1071` via
// `state.accessgroups.calendarManageAnothers`. Empty-object default keeps
// destructuring safe before bootstrap resolves.
export const accessgroupsBridge = function accessgroupsBridge(state = {}, action) {
	if (action && action.type === BOOT_OK) {
		const raw = action.payload && action.payload.accessGroups;
		return Array.isArray(raw) ? normalizeAccessGroups(raw) : (raw || {});
	}
	return state;
};

/**
 * `organizationsAccess` is the legacy per-org permission map keyed by org
 * slug. Legacy modal code reads it UNGUARDED at `calendar.event-add.js:812-814`:
 *
 *     state.organizationsAccess[URL.eventParams.org].accessGroups.calendarManageRecurrentEvent
 *
 * Required shape (derived from the legacy consumer):
 *
 *     {
 *       [orgUnique]: {
 *         accessGroups: {...permissions...},
 *         overdue: boolean,
 *         financialModule: boolean
 *       }
 *     }
 *
 * The bridge composes this from `action.meta.arg` (the original
 * `fetchBootstrap(orgUnique)` argument that the thunk was dispatched with)
 * and the resolved `action.payload`. When no orgUnique is supplied (e.g.,
 * a fetch was triggered with `null` and rejected), the bridge falls back
 * to `{}` so the legacy unguarded access still doesn't crash — it will
 * crash on the next dereference, but only on the orgUnique key, which is
 * what the consumer site is structured to handle (the URL hash always has
 * an org parameter when the modal is open).
 */
export function organizationsAccessBridge(state = {}, action) {
	if (!action || action.type !== BOOT_OK) return state;
	const orgUnique = action.meta && action.meta.arg ? action.meta.arg : null;
	if (!orgUnique) return {};
	const payload = action.payload || {};
	const organization = payload.organization || {};
	const rawGroups = payload.accessGroups;
	const currentOrgGroups = Array.isArray(rawGroups)
		? normalizeAccessGroups(rawGroups)
		: (rawGroups || {});

	// Start with current org's full permissions
	const result = {
		[orgUnique]: {
			accessGroups: currentOrgGroups,
			overdue: !!organization.overdue,
			financialModule: !!organization.financialModule
		}
	};

	// Add other orgs from organizationCalendarManageAnothersRights.
	// Legacy event-add.js:812-814 reads
	//   `state.organizationsAccess[targetOrg].accessGroups.calendarManageAnothers`
	// for cross-org event creation.
	const rights = payload.orgCalendarRights || {};
	const userOrgs = payload.userOrgs || [];
	userOrgs.forEach(org => {
		if (!org) return;
		const key = org.unique || org.organizationUnique;
		if (!key || key === orgUnique) return;
		const orgId = String(org.id || org.organizationID || '');
		result[key] = {
			accessGroups: { calendarManageAnothers: !!rights[orgId] },
			overdue: !!org.overdue,
			financialModule: !!org.financialModule
		};
	});

	return result;
}

// ── Refs-derived bridges (normalized map+list pairs) ─────────────────────────
// Legacy code expects `normalizeData()` shape: `{ [byField]: item }` map +
// `[byFieldValue, ...]` list. Symfony API returns `id` instead of legacy
// `locationID`/`resourceID`/`pilotID`, so field mapping is applied.
//
// Reference: calendar-day.js:40-81 normalizeData() calls.

const LOC_FIELD_MAP = { id: 'locationID' };
const RES_FIELD_MAP = { id: 'resourceID' };
const PILOT_FIELD_MAP = { id: 'pilotID' };

function makeRefMapBridge(payloadKey, byField, compositeKey, fieldMap) {
	return function refMapBridge(state = {}, action) {
		if (action && action.type === REFS_OK) {
			const arr = (action.payload && action.payload[payloadKey]) || [];
			return normalizeToMapList(arr, byField, compositeKey, fieldMap).map;
		}
		return state;
	};
}

function makeRefListBridge(payloadKey, byField, compositeKey, fieldMap) {
	return function refListBridge(state = [], action) {
		if (action && action.type === REFS_OK) {
			const arr = (action.payload && action.payload[payloadKey]) || [];
			return normalizeToMapList(arr, byField, compositeKey, fieldMap).list;
		}
		return state;
	};
}

// Locations need org metadata enrichment. Legacy code at
// calendar-day.event-modals.js:276-278 reads `locations[id].organizationUnique`,
// calendar.selectors.js:74-95 reads `organizationName` and `locationName`.
// Symfony Location API returns `id`, `name` but NOT these fields.
// The bridge listens to BOTH bootstrap (for org data) and refs (for locations)
// and enriches each location with org metadata from the current org.
let _cachedOrg = null;
let _cachedLocRaw = null;

function enrichLocations(rawArr, org) {
	return rawArr.map(loc => {
		const enriched = { ...loc };
		if (!enriched.locationName && enriched.name) enriched.locationName = enriched.name;
		if (!enriched.organizationUnique && org) {
			enriched.organizationUnique = org.unique || org.organizationUnique || null;
		}
		if (!enriched.organizationName && org) {
			enriched.organizationName = org.name || org.organizationName || null;
		}
		if (!enriched.organizationID && org) {
			enriched.organizationID = org.id || org.organizationID || null;
		}
		return enriched;
	});
}

function recomputeLocations() {
	if (!_cachedLocRaw || _cachedLocRaw.length === 0) return { map: {}, list: [] };
	const enriched = enrichLocations(_cachedLocRaw, _cachedOrg);
	return normalizeToMapList(enriched, 'locationID', false, LOC_FIELD_MAP);
}

export function locationsMapBridge(state = {}, action) {
	if (!action) return state;
	let recompute = false;
	if (action.type === BOOT_OK) {
		_cachedOrg = (action.payload && action.payload.organization) || null;
		recompute = true;
	}
	if (action.type === REFS_OK) {
		_cachedLocRaw = (action.payload && action.payload.locations) || [];
		recompute = true;
	}
	return recompute ? recomputeLocations().map : state;
}

export function locationsListBridge(state = [], action) {
	if (!action) return state;
	let recompute = false;
	if (action.type === BOOT_OK) {
		_cachedOrg = (action.payload && action.payload.organization) || null;
		recompute = true;
	}
	if (action.type === REFS_OK) {
		_cachedLocRaw = (action.payload && action.payload.locations) || [];
		recompute = true;
	}
	return recompute ? recomputeLocations().list : state;
}
export const resourcesMapBridge = makeRefMapBridge('resources', 'resourceID', false, RES_FIELD_MAP);
export const resourcesListBridge = makeRefListBridge('resources', 'resourceID', false, RES_FIELD_MAP);
// Composite key: a pilot holds the role in several orgs and the endpoint returns
// one row per (org, pilot). A pilotID-only key kept the last row and hid the pilot
// from the other org's filter. Legacy keyed composite too (calendar-day.js:52-58).
export const pilotsMapBridge = makeRefMapBridge(
	'pilots',
	['organizationUnique', 'pilotID'],
	true,
	PILOT_FIELD_MAP
);
export const pilotsListBridge = makeRefListBridge(
	'pilots',
	['organizationUnique', 'pilotID'],
	true,
	PILOT_FIELD_MAP
);
// Legacy mechanics use byField='id' (default), no field mapping needed.
// Custom bridge (not the factory): also listens to the lazy fetchMechanics
// thunk (day view defers mechanics off first paint), and a null mechanics
// payload — a skipMechanics refs pass — keeps prior state instead of wiping.
function makeMechanicsBridge(kind) {
	return function mechanicsBridge(state = kind === 'map' ? {} : [], action) {
		if (action && (action.type === REFS_OK || action.type === MECHANICS_OK)) {
			const arr = action.payload && action.payload.mechanics;
			if (arr == null) return state;
			const norm = normalizeToMapList(arr, 'id', false, null);
			return kind === 'map' ? norm.map : norm.list;
		}
		return state;
	};
}
export const mechanicsMapBridge = makeMechanicsBridge('map');
export const mechanicsListBridge = makeMechanicsBridge('list');
// Instructors bridge: legacy calendar-day.js:59 normalizes with composite key
// `${locationID}-${resourceID}`. In legacy SSR, "instructors" are resource objects
// (not users). Symfony resources have nested `location` objects, so we flatten
// `resource.location.id` → `locationID` and `resource.id` → `resourceID` before
// compositing. Sourced from `resources` payload (not `mechanics`).
function enrichResourcesForInstructors(rawArr) {
	return rawArr.map(res => {
		const enriched = { ...res };
		if (!enriched.resourceID) enriched.resourceID = enriched.id;
		if (!enriched.locationID) {
			if (enriched.location && typeof enriched.location === 'object') {
				enriched.locationID = enriched.location.id;
			} else if (typeof enriched.location === 'number' || typeof enriched.location === 'string') {
				enriched.locationID = enriched.location;
			}
		}
		return enriched;
	});
}

export function instructorsMapBridge(state = {}, action) {
	if (action && action.type === REFS_OK) {
		const arr = (action.payload && action.payload.resources) || [];
		const enriched = enrichResourcesForInstructors(arr);
		return normalizeToMapList(enriched, ['locationID', 'resourceID'], true, null).map;
	}
	return state;
}

export function instructorsListBridge(state = [], action) {
	if (action && action.type === REFS_OK) {
		const arr = (action.payload && action.payload.resources) || [];
		const enriched = enrichResourcesForInstructors(arr);
		return normalizeToMapList(enriched, ['locationID', 'resourceID'], true, null).list;
	}
	return state;
}
// customFields — legacy `getCustomFields()` at calendar.model.js:332-341 filters by
// `customFields[fieldID].organizationUnique === org`. Symfony returns `id`, `name`,
// `type`, `required` but NO `organizationUnique`. Bridge enriches from bootstrap org,
// same pattern as locations enrichment.
let _cachedCfOrg = null;
let _cachedCfRaw = null;

function enrichCustomFields(rawArr, org) {
	return rawArr.map(cf => {
		const enriched = { ...cf };
		if (!enriched.organizationUnique && org) {
			enriched.organizationUnique = org.unique || org.organizationUnique || null;
		}
		return enriched;
	});
}

function recomputeCustomFields() {
	if (!_cachedCfRaw || _cachedCfRaw.length === 0) return { map: {}, list: [] };
	const enriched = enrichCustomFields(_cachedCfRaw, _cachedCfOrg);
	return normalizeToMapList(enriched, 'id', false, null);
}

export function customFieldsMapBridge(state = {}, action) {
	if (!action) return state;
	let recompute = false;
	if (action.type === BOOT_OK) {
		_cachedCfOrg = (action.payload && action.payload.organization) || null;
		recompute = true;
	}
	// Custom fields are sourced ONLY from the standalone `fetchCustomFields`
	// thunk (CF_OK). `fetchRefs` (REFS_OK) no longer fetches them (network
	// dedup), and reacting to REFS_OK would blank the data the late, slower
	// refs batch arrives carrying no customFields.
	if (action.type === CF_OK) {
		_cachedCfRaw = (action.payload && action.payload.customFields) || [];
		recompute = true;
	}
	return recompute ? recomputeCustomFields().map : state;
}

export function customFieldsListBridge(state = [], action) {
	if (!action) return state;
	let recompute = false;
	if (action.type === BOOT_OK) {
		_cachedCfOrg = (action.payload && action.payload.organization) || null;
		recompute = true;
	}
	// CF_OK only — see `customFieldsMapBridge` note (REFS_OK no longer carries
	// customFields after the network dedup).
	if (action.type === CF_OK) {
		_cachedCfRaw = (action.payload && action.payload.customFields) || [];
		recompute = true;
	}
	return recompute ? recomputeCustomFields().list : state;
}

/**
 * Bundle every bridge under the legacy key names so the entrypoint can
 * spread it directly into the createStore reducer map. Keys must match the
 * top-level state slots that legacy `CalendarNav` / `EventModals` consume
 * via `connect`.
 */
export const calendarMonthBridgeReducers = {
	organization: organizationBridge,
	organizations: organizationsMapBridge,
	organizationsList: organizationsListBridge,
	user: userBridge,
	timezone: timezoneBridge,
	accessgroups: accessgroupsBridge,
	organizationsAccess: organizationsAccessBridge,
	resources: resourcesMapBridge,
	resourcesList: resourcesListBridge,
	locations: locationsMapBridge,
	locationsList: locationsListBridge,
	customFields: customFieldsMapBridge,
	customFieldsList: customFieldsListBridge,
	pilots: pilotsMapBridge,
	pilotsList: pilotsListBridge,
	instructors: instructorsMapBridge,
	instructorsList: instructorsListBridge,
	mechanics: mechanicsMapBridge,
	mechanicsList: mechanicsListBridge
};
